import { Dialog, QuestionDialog, useDialogManager } from '../../widgets/dialog';
import { Icon } from '../../widgets/icon';
import { Link, TextButton } from '../../widgets/buttons';
import { removeNotification, showErrorNotification, showNotification, showSuccessNotification } from '../../widgets/notificationToaster';
import { Row, Table, TableHeading } from '../../widgets/table';
import { ZTooltip } from '../../widgets/ztooltip';
import { truncate } from 'lodash';
import React, { FC } from 'react';
import { graphql, useMutation } from 'react-relay';
import { stringFormat, T } from 'src/shared/helpers';
import LinkIcon from 'src/shared/material_icons/action/open_in_new.svg';
import styled from 'styled-components';
import { ECommerce } from '../salesChannels/__generated__/salesChannelsActive.graphql';
import { generateStoreNumbers } from '../salesChannels/helpers';
import {
    productSalesChannelListDeleteCodeMutation,
    productSalesChannelListDeleteCodeMutation$data
} from './__generated__/productSalesChannelListDeleteCodeMutation.graphql';
import { AdditionalCodes, ProductSalesChannel } from './productList';
import SalesChannelIcon from './salesChannelIcon';

const StyledDivElementTable = styled.div`
    display: flex;
    height: 100%;
    align-items: center;
    max-width: 100%;
    font-size: 14px;
`;

const NameCell = styled.div`
    white-space: nowrap;
    font-size: 14px;
`;

const StyledLinkContainer = styled.div`
    /* display: flex;
    flex-direction: column;
    justify-content: flex-start;
    width: 100%; */
`;

const StyledLink = styled(Link)`
    font-weight: 400;

    svg {
        stroke-width: 0.2px;
        margin-bottom: 3px;
        margin-left: 5px;
        vertical-align: middle;
        display: inline-block;
    }
`;

const StyledSpan = styled.span`
    font-weight: 400;
`;

const StyledImg = styled.div`
    img {
        width: 55px;
        height: 55px;
    }

    [class*='StyledBackgroundCircle'] {
        width: 55px !important;
        height: 55px !important;
    }
`;

const ActionsColContainer = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
`;

export const UnclickableTextButton = styled.span`
    color: #aaa;
    font-size: 13px;
    font-weight: 700;
    cursor: default;
`;

const BrokenBadge = styled.span`
    display: inline-flex;
    margin-left: 8px;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
    background: #ec7063;
    color: #ffffff;
`;

interface Props {
    additionalCodes: AdditionalCodes;
    salesChannel: ProductSalesChannel;
    productID: string;
    code: string;
}

interface SalesChannel {
    salesChannelID: string;
    storePosition: number | null | undefined;
    productCode: string;
    ecommerceName: ECommerce | undefined;
    name: string | null | undefined;
    remoteUrl: string | null | undefined;
    definitiveUrl?: string;
    isOtherSalesChannel: boolean;
    isBroken: boolean;
}

interface DisconnectProductSalesChannelProps {
    productID: string;
    sku: string;
    salesChannelID: string;
    onRemoveCompleted?: () => void;
}

const deleteCodeMutation = graphql`
    mutation productSalesChannelListDeleteCodeMutation($modelID: ID!, $code: String!, $salesChannelID: ID!) {
        deleteModelCode(modelID: $modelID, code: $code, salesChannelID: $salesChannelID) {
            id
            modelID
            additionalCodes {
                code
                salesChannelID
            }
        }
    }
`;

export const DisconnectProductSalesChannel = (props: DisconnectProductSalesChannelProps) => {
    const [deleteCode, isInFightDeleteCode] = useMutation<productSalesChannelListDeleteCodeMutation>(deleteCodeMutation);
    const { showDialog, closeDialog } = useDialogManager();

    return (
        <QuestionDialog
            message={stringFormat(T._('This will disconnect this product from the selected sales channel. Do you want to proceed?', 'Admin'))}
            onYesClick={() => {
                closeDialog('delete-channel');
                const remoteSalesChannellNotification = showNotification({
                    type: 'default',
                    message: T._('Removing sales channel in progress...', 'Admin')
                });
                deleteCode({
                    variables: {
                        modelID: props.productID,
                        code: props.sku,
                        salesChannelID: props.salesChannelID
                    },
                    onError: () => {
                        showErrorNotification({ message: T._('Failed remove sales channel.', 'Admin') });
                        removeNotification(remoteSalesChannellNotification);
                    },
                    updater: (store) => {
                        store.invalidateStore();
                    },
                    onCompleted: (resp: productSalesChannelListDeleteCodeMutation$data) => {
                        removeNotification(remoteSalesChannellNotification);
                        showSuccessNotification({ message: T._('Sales channel disconnected.', 'Admin') });
                        if (props.onRemoveCompleted) props.onRemoveCompleted();
                        closeDialog('product-sales-channels');
                    }
                });
            }}
        />
    );
};

const ProductSalesChannelList: FC<Props> = ({ additionalCodes, salesChannel, productID, code }) => {
    const { showDialog, closeDialog } = useDialogManager();

    const getSalesChannelIDs = () => {
        const ids: string[] = [];

        ids.push(salesChannel.id);

        additionalCodes
            .filter((ac) => ac.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE')
            .forEach((code) => {
                ids.push(code.salesChannel.id);
            });

        return ids;
    };

    const salesChannelIDs = getSalesChannelIDs();

    const storeNumbers = generateStoreNumbers(salesChannelIDs);

    const getSalesChannelsByProduct = () => {
        let salesChannels: SalesChannel[] = [];

        if (salesChannel.ecommerceSettings?.ecommerceName !== 'NONE') {
            salesChannels.push({
                salesChannelID: salesChannel.id,
                storePosition: salesChannel.detail?.properties?.storePosition,
                productCode: code,
                ecommerceName: salesChannel.ecommerceSettings?.ecommerceName,
                name: salesChannel.detail?.properties?.name,
                remoteUrl: salesChannel.ecommerceSettings?.remoteUrl,
                isOtherSalesChannel: false,
                definitiveUrl: salesChannel.ecommerceSettings?.definitiveUrl || '',
                isBroken: salesChannel.ecommerceSettings === null
            });
        }

        additionalCodes.forEach((code) => {
            if (code.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE') {
                salesChannels.push({
                    salesChannelID: code.salesChannel.id,
                    storePosition: code.salesChannel.detail?.properties?.storePosition,
                    productCode: code.code,
                    ecommerceName: code.salesChannel.ecommerceSettings?.ecommerceName,
                    name: code.salesChannel.detail?.properties?.name,
                    remoteUrl: code.salesChannel.ecommerceSettings?.remoteUrl,
                    isOtherSalesChannel: true,
                    definitiveUrl: code.salesChannel.ecommerceSettings?.definitiveUrl || '',
                    isBroken: code.salesChannel.ecommerceSettings === null
                });
            }
        });

        return salesChannels;
    };

    const headings = [
        ['#', '0.73fr'],
        [T._('Store Name', 'Admin'), '1.21fr'],
        [T._('Code', 'Admin'), '1fr'],
        [T._('Url', 'Admin'), '1.53fr'],
        [T._('Platform', 'Admin'), '0.70fr'],
        ['', '0.83fr']
    ] as TableHeading[];

    const buildRowCells = (salesChannel: SalesChannel) => {
        const name: string = salesChannel.name || '';

        const nameMaxLength = window.innerWidth < 1900 ? 20 : 30;
        const urlMaxLength = window.innerWidth < 1900 ? 30 : 35;
        const storeUrl = salesChannel.definitiveUrl || '';

        const storeNumber = salesChannel.storePosition ?? storeNumbers.find((x) => x.salesChannelID === salesChannel.salesChannelID)?.storeNumber ?? 1;

        return [
            <StyledDivElementTable>{T._(`Store ${storeNumber}`, 'Admin')}</StyledDivElementTable>,
            <>
                {name && name.length > nameMaxLength ? (
                    <ZTooltip position={'top'} title={name}>
                        <NameCell>{name && name.length > nameMaxLength ? truncate(name) : name}</NameCell>
                    </ZTooltip>
                ) : (
                    <NameCell>{name}</NameCell>
                )}
            </>,
            <StyledDivElementTable>{salesChannel.productCode}</StyledDivElementTable>,
            <StyledDivElementTable>
                <StyledLinkContainer>
                    <StyledLink href={storeUrl} target='_blank'>
                        {storeUrl && storeUrl?.length > urlMaxLength ? truncate(storeUrl, { length: urlMaxLength }) : storeUrl || ''}
                        <Icon>
                            <LinkIcon></LinkIcon>
                        </Icon>
                    </StyledLink>
                </StyledLinkContainer>
            </StyledDivElementTable>,
            <StyledDivElementTable>
                <StyledImg>{SalesChannelIcon(salesChannel.ecommerceName, false)}</StyledImg>
                {salesChannel.isBroken && (
                    <ZTooltip
                        position={'top'}
                        interactive={true}
                        html={
                            <span>
                                {T._(
                                    'This product is no longer functioning on the websites where it is published because the originating sales channel has been removed. Please contact ',
                                    'Admin'
                                )}
                                <Link href='mailto:help@zakeke.com'>help@zakeke.com</Link>
                                {T._(' for assistance.', 'Admin')}
                            </span>
                        }
                    >
                        <BrokenBadge>{T._('broken', 'Admin')}</BrokenBadge>
                    </ZTooltip>
                )}
            </StyledDivElementTable>,
            <ActionsColContainer>
                {salesChannel.isOtherSalesChannel ? (
                    <TextButton
                        onClick={() => {
                            showDialog(
                                'delete-channel',
                                <DisconnectProductSalesChannel
                                    productID={productID}
                                    sku={salesChannel.productCode}
                                    salesChannelID={salesChannel.salesChannelID}
                                ></DisconnectProductSalesChannel>
                            );
                        }}
                    >
                        {T._('Disconnect', 'Admin')}
                    </TextButton>
                ) : (
                    <ZTooltip
                        position={'top'}
                        title={T._(
                            'You cannot disconnect a product from the channel from which it was originally created. If you no longer wish to offer this product as customizable, you can either unpublish it or delete it.',
                            'Admin'
                        )}
                    >
                        <UnclickableTextButton>{T._('Disconnect', 'Admin')}</UnclickableTextButton>
                    </ZTooltip>
                )}
            </ActionsColContainer>
        ];
    };

    return (
        <Dialog
            onClose={() => {
                closeDialog('product-sales-channels');
            }}
            size='large'
        >
            <Table headings={headings}>
                {getSalesChannelsByProduct().map((salesChannel, i) => {
                    return <Row key={i.toString()} cells={buildRowCells(salesChannel)} />;
                })}
            </Table>
        </Dialog>
    );
};

export default ProductSalesChannelList;
